import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Card,
  Form,
  Spinner,
  Pagination as BSPagination,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { format, subDays, startOfMonth, startOfDay, endOfDay, isWithinInterval, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import NavContainer from './Nav';
import OffCanvasNav from './OffCanvasNav';
import { dotoniwoofApi } from '../api/dotoniwooApi';
import Pagination from './Pagination';
import SalesDetailSection from './SalesDetailSection';
import './Sales.css';

const ITEMS_PER_PAGE = 50;
const LIMIT_WHEN_FILTERING = 200;

const getLocalDateString = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getSaleDateStr = (sale) => {
  const raw = sale.sale_date;
  if (!raw) return '';
  if (typeof raw === 'string' && raw.length >= 10) return raw.slice(0, 10);
  try {
    const dt = typeof raw === 'string' ? parseISO(raw) : new Date(raw);
    return format(dt, 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

const PRESETS = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'ayer', label: 'Ayer' },
  { id: 'semana', label: 'Esta semana' },
  { id: 'mes', label: 'Este mes' },
  { id: 'custom', label: 'Personalizado' },
  { id: '', label: 'Todos' },
];

const Sales = () => {
  const [sales, setSales] = useState([]);
  const [saleDetail, setSaleDetail] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const [preset, setPreset] = useState('hoy');
  const [customDate, setCustomDate] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');

  const [todayKpi, setTodayKpi] = useState({
    total: 0,
    transactions: 0,
    avgTicket: 0,
    vsYesterday: null,
    topProduct: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const todayStr = useMemo(() => getLocalDateString(), []);
  const yesterdayStr = useMemo(() => getLocalDateString(subDays(new Date(), 1)), []);
  const weekStart = useMemo(() => startOfDay(subDays(new Date(), 6)), []);
  const monthStart = useMemo(() => startOfMonth(new Date()), []);

  const fetchTodayKpis = useCallback(async () => {
    try {
      const [todayRes, kpiRes] = await Promise.all([
        dotoniwoofApi.get(`/sale.php/sale-today?date=${todayStr}`),
        dotoniwoofApi.get(`/sale.php/sale-kpi-today?date=${todayStr}`).catch(() => ({ data: {} })),
      ]);
      const total = (todayRes.data?.status === 'ok' && todayRes.data?.total != null)
        ? Number(todayRes.data.total)
        : 0;
      const kpis = kpiRes.data?.status === 'ok' && kpiRes.data?.kpis
        ? kpiRes.data.kpis
        : {};
      const tx = Number(kpis.transactions) || 0;
      const avg = Number(kpis.avg_ticket);
      setTodayKpi({
        total,
        transactions: tx,
        avgTicket: !Number.isNaN(avg) && avg > 0 ? avg : (total && tx ? total / tx : 0),
        vsYesterday: kpis.ventasVsDiaAnterior != null ? Number(kpis.ventasVsDiaAnterior) : null,
        topProduct: kpis.topProducto ?? null,
      });
    } catch (e) {
      console.error('Error fetching today KPIs:', e);
    }
  }, [todayStr]);

  const fetchSales = useCallback(async (page, limit = ITEMS_PER_PAGE) => {
    setLoading(true);
    try {
      const res = await dotoniwoofApi.get(`/sale.php?page=${page}&limit=${limit}`);
      const data = res.data;
      if (data?.code === 200 && Array.isArray(data.message)) {
        setSales(data.message);
        setTotalPages(data.total_pages ?? Math.max(1, Math.ceil((data.total_rows || data.message.length) / limit)));
      } else {
        setSales([]);
        setTotalPages(1);
      }
    } catch (e) {
      console.error('Error fetching sales:', e);
      setSales([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTodayKpis();
  }, [fetchTodayKpis]);

  useEffect(() => {
    if (preset === '') {
      fetchSales(currentPage, ITEMS_PER_PAGE);
    } else {
      const needBig = ['hoy', 'ayer', 'semana', 'mes', 'custom'].includes(preset);
      fetchSales(1, needBig ? LIMIT_WHEN_FILTERING : ITEMS_PER_PAGE);
    }
  }, [preset, currentPage, fetchSales]);

  const filteredSales = useMemo(() => {
    let list = [...sales];
    const dStr = getSaleDateStr;
    if (preset === 'hoy') {
      list = list.filter((s) => dStr(s) === todayStr);
    } else if (preset === 'ayer') {
      list = list.filter((s) => dStr(s) === yesterdayStr);
    } else if (preset === 'semana') {
      list = list.filter((s) => {
        const str = dStr(s);
        if (!str) return false;
        try {
          const d = parseISO(str);
          return isWithinInterval(d, { start: weekStart, end: endOfDay(new Date()) });
        } catch {
          return false;
        }
      });
    } else if (preset === 'mes') {
      list = list.filter((s) => {
        const str = dStr(s);
        if (!str) return false;
        try {
          const d = parseISO(str);
          return isWithinInterval(d, { start: monthStart, end: endOfDay(new Date()) });
        } catch {
          return false;
        }
      });
    } else if (preset === 'custom') {
      if (customDate) {
        list = list.filter((s) => dStr(s) >= customDate && (!customDateEnd || dStr(s) <= customDateEnd));
      }
    }
    return list;
  }, [sales, preset, todayStr, yesterdayStr, weekStart, monthStart, customDate, customDateEnd]);

  const periodTotal = useMemo(
    () => filteredSales.reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0),
    [filteredSales]
  );

  const totalFilteredPages = Math.max(1, Math.ceil(filteredSales.length / ITEMS_PER_PAGE));
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredSales.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSales, currentPage]);

  const effectiveTotalPages = preset === '' ? totalPages : totalFilteredPages;
  const effectivePage = preset === '' ? currentPage : Math.min(currentPage, totalFilteredPages);
  const displayList = preset === '' ? sales : paginatedList;

  useEffect(() => {
    if (preset !== '') setCurrentPage(1);
  }, [preset, customDate, customDateEnd]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleShowDetail = async (sale) => {
    setSelectedSale(sale);
    setShowDetail(true);
    setDetailLoading(true);
    try {
      const res = await dotoniwoofApi.get(`/saleDetail.php/${sale.id_sale}`);
      if (res.data?.code === 200 && Array.isArray(res.data.message)) {
        setSaleDetail(res.data.message);
      } else {
        setSaleDetail([]);
      }
    } catch (e) {
      console.error('Error fetching sale detail:', e);
      setSaleDetail([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setShowDetail(false);
    setSaleDetail([]);
    setSelectedSale(null);
  };

  const formatMoney = (n) =>
    (Number(n) ?? 0).toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const hasFilter = preset !== '' || (preset === 'custom' && (customDate || customDateEnd));
  const clearFilters = () => {
    setPreset('');
    setCustomDate('');
    setCustomDateEnd('');
    setCurrentPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Resumen de ventas - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />

      <div className="sales-container">
        <div className="page-header">
          <h1 className="page-title">Resumen de ventas</h1>
        </div>

        {/* KPIs del día */}
        <div className="sales-kpi-row">
          <div className="sales-kpi-card">
            <h6>Ventas hoy</h6>
            <div className="sales-kpi-value">{formatMoney(todayKpi.total)}</div>
            <small className="text-muted">{format(new Date(), "EEEE d 'de' MMMM", { locale: es })}</small>
          </div>
          <div className="sales-kpi-card">
            <h6>Transacciones hoy</h6>
            <div className="sales-kpi-value sales-kpi-muted">{todayKpi.transactions}</div>
          </div>
          <div className="sales-kpi-card">
            <h6>Ticket promedio</h6>
            <div className="sales-kpi-value sales-kpi-muted">{formatMoney(todayKpi.avgTicket)}</div>
          </div>
          {todayKpi.vsYesterday != null && (
            <div className="sales-kpi-card">
              <h6>Vs. ayer</h6>
              <div className="sales-kpi-value sales-kpi-muted">
                {todayKpi.vsYesterday >= 0 ? '+' : ''}{todayKpi.vsYesterday}%
              </div>
              <span
                className={`sales-kpi-badge ${todayKpi.vsYesterday >= 0 ? 'sales-kpi-up' : 'sales-kpi-down'}`}
              >
                {todayKpi.vsYesterday >= 0 ? '↑' : '↓'} {todayKpi.vsYesterday >= 0 ? 'Sube' : 'Baja'}
              </span>
            </div>
          )}
        </div>

        {/* Filtros modernos */}
        <div className="sales-filters-wrap">
          <div className="sales-filters-bar">
            {PRESETS.map((p) => (
              <button
                key={p.id || 'all'}
                type="button"
                className={`sales-filter-chip ${preset === p.id ? 'sales-filter-chip-active' : ''}`}
                onClick={() => {
                  setPreset(p.id);
                  setCurrentPage(1);
                  if (p.id !== 'custom') {
                    setCustomDate('');
                    setCustomDateEnd('');
                  }
                }}
              >
                {p.label}
              </button>
            ))}
            {preset === 'custom' && (
              <div className="sales-filter-date-wrap">
                <Form.Control
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  placeholder="Desde"
                />
                <Form.Control
                  type="date"
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                  placeholder="Hasta"
                />
              </div>
            )}
            {hasFilter && (
              <button type="button" className="sales-filter-clear" onClick={clearFilters}>
                × Limpiar
              </button>
            )}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              className="sales-loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Spinner animation="border" />
              <span>Cargando ventas…</span>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <Card className="sales-card">
                <Card.Header className="sales-card-header">
                  <h5>Listado de ventas</h5>
                </Card.Header>
                {hasFilter && (
                  <div className="sales-period-summary">
                    <div className="sales-period-summary-item">
                      <span className="label">Total del período</span>
                      <span className="value">{formatMoney(periodTotal)}</span>
                    </div>
                    <div className="sales-period-summary-item">
                      <span className="label">Ventas</span>
                      <span className="value">{filteredSales.length}</span>
                    </div>
                  </div>
                )}
                <div className="sales-table-wrap">
                  {displayList.length === 0 ? (
                    <div className="sales-empty">
                      <div className="empty-icon">🛒</div>
                      <p className="mb-0">
                        {sales.length === 0
                          ? 'No hay ventas registradas.'
                          : 'No hay ventas que coincidan con los filtros.'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <Table className="sales-table" responsive>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Fecha</th>
                            <th>Total</th>
                            <th>Estado</th>
                            <th className="text-end">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayList.map((sale) => (
                            <tr key={sale.id_sale}>
                              <td>{sale.id_sale}</td>
                              <td>{getSaleDateStr(sale) || sale.sale_date}</td>
                              <td className="amount-cell">{formatMoney(sale.total_amount)}</td>
                              <td>{sale.status ?? '—'}</td>
                              <td className="text-end">
                                <Button
                                  size="sm"
                                  className="btn-detail"
                                  onClick={() => handleShowDetail(sale)}
                                >
                                  Ver detalle
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {effectiveTotalPages > 1 && (
                        <div className="sales-pagination-wrap">
                          <small className="text-muted">
                            Página {preset === '' ? currentPage : effectivePage} de {effectiveTotalPages}
                            {preset !== '' && ` · ${filteredSales.length} ventas`}
                          </small>
                          {preset === '' ? (
                            <BSPagination>
                              <BSPagination.First
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                              />
                              <BSPagination.Prev
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              />
                              {[...Array(effectiveTotalPages).keys()].map((i) => (
                                <BSPagination.Item
                                  key={i + 1}
                                  active={i + 1 === currentPage}
                                  onClick={() => handlePageChange(i + 1)}
                                >
                                  {i + 1}
                                </BSPagination.Item>
                              ))}
                              <BSPagination.Next
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === effectiveTotalPages}
                              />
                              <BSPagination.Last
                                onClick={() => handlePageChange(effectiveTotalPages)}
                                disabled={currentPage === effectiveTotalPages}
                              />
                            </BSPagination>
                          ) : (
                            <Pagination
                              currentPage={effectivePage}
                              totalPages={effectiveTotalPages}
                              paginate={handlePageChange}
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Detalle de ventas (tabla contable) */}
        <SalesDetailSection />
      </div>

      <Modal show={showDetail} onHide={closeModal} centered className="sales-modal">
        <Modal.Header closeButton>
          <Modal.Title>Detalle de la venta {selectedSale?.id_sale}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="sales-loading">
              <Spinner animation="border" size="sm" />
              <span>Cargando detalle…</span>
            </div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Precio</th>
                  <th>Peso</th>
                  <th>Color</th>
                  <th>Talla</th>
                </tr>
              </thead>
              <tbody>
                {saleDetail.map((row) => (
                  <tr key={row.id_sale_detail}>
                    <td>{row.id_sale_detail}</td>
                    <td>{row.description}</td>
                    <td>{formatMoney(row.unit_price)}</td>
                    <td>{row.weight_name ?? '—'}</td>
                    <td>{row.color_name ?? '—'}</td>
                    <td>{row.size_name ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Sales;
